/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA ;
 */
import { __ } from '@oat-sa-private/ui-core';
import { supportedMetadataTypes } from '@/constants/metadata';

/**
 * Validates the metadata key value
 * @param {string} metadataKey key - Metadata key to validate
 * @returns {boolean} true if the metadata is valid
 */
export function validateMetadataKey(metadataKey) {
    // regular expression for metadata key validation
    const regex = /\./;

    // test the metadata key against the regex and return the result
    return !regex.test(metadataKey);
}

const defaultNumericPattern = /^\d+$/;

/**
 * Get the required option for item depending on validation conditions
 * @param {Object} schema
 * @param {Object} item
 * @returns {boolean} true if the item is required, false otherwise
 */
export const getItemRequired = (schema, item) => {
    const { key, required = false } = item || {};
    const requirementCondition = schema.groupValidationConditions?.find(
        ({ target, rule, valid }) => target === key && rule === 'notRequiredIf' && valid?.length
    );

    return requirementCondition ? false : !!required;
};

const validateNumericField = (value, validationRegexp) =>
    validationRegexp ? !!value.match(validationRegexp) : defaultNumericPattern.test(value);

/**
 * Validate the number input
 * @param {Object} params
 * @param {string} params.value - value to validate
 * @param {Object} params.itemDefinition - schema item
 * @returns {Object} object with validity and message
 */
export const numberValidator = ({ value, itemDefinition: item }) => {
    const { validationRegexp } = item;
    try {
        const msg = validationRegexp ? __('The value does not follow the pattern') : __('Only numbers allowed');
        const numberValidity =
            !value || validateNumericField(value, validationRegexp) ? { valid: true } : { valid: false, msg };

        return numberValidity;
    } catch (e) {
        return { valid: false, msg: __('Validation pattern is not supported') };
    }
};

/**
 * Validate the text input
 * @param {Object} params
 * @param {string} params.value - value to validate
 * @param {Object} params.itemDefinition - schema item
 * @returns {Object} object with validity and message
 */
export const textValidator = ({ value, itemDefinition: item }) => {
    const { validationRegexp } = item;
    if (validationRegexp) {
        try {
            const textValidity =
                !value || !!value.match(validationRegexp)
                    ? { valid: true }
                    : { valid: false, msg: __('The value does not follow the pattern') };

            return textValidity;
        } catch (e) {
            return { valid: false, msg: __('Validation pattern is not supported') };
        }
    }
    return { valid: true };
};

/**
 * Validate the enum input
 * @param {Object} params
 * @param {string} params.value - value to validate
 * @param {Object} params.itemDefinition - schema item
 * @returns {Object} object with validity and message
 */
export const enumValidator = ({ value, itemDefinition: item }) => {
    if (!item.source || item.source.length === 0 || (value && !item.source.includes(value))) {
        return { valid: false, msg: __('The value is not in the list') };
    }
    return { valid: true };
};

const validators = {
    [supportedMetadataTypes.TEXT]: textValidator,
    [supportedMetadataTypes.NUMBER]: numberValidator,
    [supportedMetadataTypes.ENUM]: enumValidator
};

/**
 * Validate the field
 * @param {Object} params
 * @param {Object} params.itemDefinition - schema item definition
 * @param {string} params.value - metadata value to validate
 * @returns {Object} object with validity and message
 */
const validateItemValue = ({ itemDefinition, value }) =>
    itemDefinition && validators[itemDefinition.type]
        ? validators[itemDefinition.type]({ value, itemDefinition })
        : { valid: true };

/**
 * Validate the metadata input validity
 * @param {Object} params
 * @param {Object} params.schema - metadata schema
 * @param {string} params.key - metadata key
 * @param {Object} params.metadata - metadata value
 * @returns {Object} object with validity and message
 */
export const metadataSchemaInputValidity = ({ schema, key, metadata }) => {
    const value = metadata[key];
    const itemDefinition = schema.items.find(({ key: itemKey }) => itemKey === key);

    if (value) {
        // not empty? just validate format
        return validateItemValue({ itemDefinition, value });
    }

    const notRequiredIfRuleConditions = schema.groupValidationConditions?.filter(
        ({ target, rule, valid }) => target === key && rule === 'notRequiredIf' && valid?.length
    );

    if (!notRequiredIfRuleConditions || !notRequiredIfRuleConditions.length) {
        // required and empty or no conditions simply return result
        return itemDefinition?.required ? { valid: false, msg: __('This field is required') } : { valid: true };
    }

    return { valid: true };
};

export const isSchemaValidatable = schema =>
    schema && schema.items && Array.isArray(schema.items) && schema.items.length > 0;

/**
 * Validate the metadata according to the schema
 * @param {Object} schema - metadata schema
 * @param {Object} metadata - metadata value
 * @returns {boolean} true if the metadata is valid, false otherwise
 */
export const metadataSchemaValidate = (schema, metadata) => {
    if (!isSchemaValidatable(schema)) {
        return true;
    }
    const itemKeys = schema.items.map(({ key }) => key);
    const validationResults = itemKeys.reduce((acc, key) => {
        acc[key] = metadataSchemaInputValidity({ schema, key, metadata });
        return acc;
    }, {});
    return Object.keys(validationResults).every(key => validationResults[key].valid);
};

/**
 * Map metadata from the server to the client
 * @param {Object} metadata - metadata from the server
 * @returns {Object} metadata for the client
 */
// functionality can be extended in order to mutate/default the metadata coming from the server
export const mapMetadataToClient = (metadata = {}) => {
    if (!isSchemaValidatable(metadata)) {
        return {};
    }
    const supportedTypes = Object.values(supportedMetadataTypes);
    const { items } = metadata;
    const metadataItems = items.filter(item => {
        item.type = item.type || 'text';
        if (!item || typeof item !== 'object' || !item.key || !item.type || !supportedTypes.includes(item.type)) {
            return false;
        }

        return true;
    });
    return {
        ...metadata,
        items: metadataItems
    };
};
